import type {
  BootstrapRequest,
  BootstrapResponse,
  IngestEventsRequest,
  IngestEventsResponse,
  PopupSubmitRequest,
  PopupSubmitResponse,
  VisitorChatRequest,
  VisitorChatResponse,
} from '@intent/shared';
import type { WidgetConfig } from './config';

/**
 * HTTP transport.
 *
 * Events travel over plain HTTP rather than the socket so tracking survives a
 * dropped connection and, critically, a closing tab — `sendBeacon` is the only
 * mechanism a browser guarantees during unload.
 */
export class Transport {
  constructor(private readonly config: WidgetConfig) {}

  private url(path: string): string {
    return `${this.config.apiUrl}${path}`;
  }

  async bootstrap(payload: BootstrapRequest): Promise<BootstrapResponse> {
    const response = await fetch(this.url('/widget/bootstrap'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Project-Key': this.config.projectKey,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Widget bootstrap failed with status ${response.status}`);
    }

    return (await response.json()) as BootstrapResponse;
  }

  async sendEvents(payload: IngestEventsRequest): Promise<IngestEventsResponse> {
    const response = await fetch(this.url('/events'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Project-Key': this.config.projectKey,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });

    if (!response.ok) {
      throw new Error(`Event ingest failed with status ${response.status}`);
    }

    return (await response.json()) as IngestEventsResponse;
  }

  async sendChatMessage(payload: VisitorChatRequest): Promise<VisitorChatResponse> {
    const response = await fetch(this.url('/chat'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Project-Key': this.config.projectKey,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Sending the message failed with status ${response.status}`);
    }

    return (await response.json()) as VisitorChatResponse;
  }

  async submitPopupEmail(payload: PopupSubmitRequest): Promise<PopupSubmitResponse> {
    const response = await fetch(this.url('/popup/submit'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Project-Key': this.config.projectKey,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Submitting the popup failed with status ${response.status}`);
    }

    return (await response.json()) as PopupSubmitResponse;
  }

  /**
   * Fire-and-forget delivery for unload-time batches.
   *
   * `sendBeacon` cannot set headers, so the project key rides in the query
   * string. The body is sent as `text/plain` deliberately: it is a
   * CORS-safelisted content type and therefore needs no preflight, which a
   * beacon has no opportunity to perform. The API parses text/plain as JSON.
   */
  sendEventsBeacon(payload: IngestEventsRequest): boolean {
    if (typeof navigator.sendBeacon !== 'function') return false;

    const url = `${this.url('/events')}?projectKey=${encodeURIComponent(this.config.projectKey)}`;
    const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain;charset=UTF-8' });

    try {
      return navigator.sendBeacon(url, blob);
    } catch {
      return false;
    }
  }
}
